import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { PERMISSIONS } from "./constants/roles"; // Import your roles

// Automatically scroll window to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

// Components & Pages
import Layout from './components/Layout';
import RoleGuard from './components/RoleGuard';
import ProtectedRoute from './components/protectedroutes';
import AuthCallback from './AuthCallback';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Machines from './pages/Machines';
import Plants from './pages/Plants';
import PlantDetail from './pages/PlantDetail';
import MachineDetail from './pages/MachineDetail';
import SOPs from './pages/SOPs';
import SOPViewer from './pages/SOPViewer';
import Maintenance from './pages/Maintenance';
import MaintenanceExecution from './pages/MaintenanceExecution';
import Assistant from './pages/Assistant';
import Knowledge from './pages/Knowledge';
import DocumentUpload from './pages/DocumentUpload';
import PredictiveInsights from './pages/PredictiveInsights';
import Training from './pages/Training';
import Workflows from './pages/Workflows';
import Settings from './pages/Settings';
import KnowledgeDetail from './pages/KnowledgeDetail';
import AddMachine from './pages/AddMachine';
import CreateSOP from './pages/CreateSOP';
import MaintenanceDetail from './pages/MaintenanceDetail';
import ScheduleMaintenance from './components/ScheduleMaintenance';
import ErrorLogList from './pages/ErrorLogList';
import LogError from './components/LogError';
import SOPForm from './pages/SOPForm';
import WorkOrders from './pages/WorkOrders';
import WorkOrderDetail from './pages/WorkOrderDetail';
import WorkOrderCreate from './pages/WorkOrderCreate';

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public / Unprotected OAuth and Login routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes wrapped with BFF session ProtectedRoute */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* SHARED ROUTES: Accessible to all authenticated users */}
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="machines" element={<Machines />} />
        <Route path="plants" element={<Plants />} />
        <Route path="plants/:id" element={<PlantDetail />} />
        <Route path="machines/:id" element={<MachineDetail />} />
        <Route path="sops" element={<SOPs />} />
        <Route path="sops/:id" element={<SOPViewer />} />
        <Route path="assistant" element={<Assistant />} />
        <Route path="settings" element={<Settings />} />

        {/* MAINTENANCE EXECUTION GROUP: Technicians & Operators */}
        <Route
          element={<RoleGuard allowedRoles={PERMISSIONS.EXECUTE_MAINTENANCE} />}
        >
          <Route path="maintenance" element={<Maintenance />} />
          <Route
            path="maintenance/:id/execute"
            element={<MaintenanceExecution />}
          />
          <Route path="maintenance/:id" element={<MaintenanceDetail />} />
          <Route path="work-orders" element={<WorkOrders />} />
          <Route path="work-orders/:id" element={<WorkOrderDetail />} />
          <Route path="errors/report" element={<LogError />} />
        </Route>

        {/* MANAGEMENT GROUP: Admin & Managers only */}
        <Route
          element={<RoleGuard allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE} />}
        >
          <Route
            path="maintenance/schedule"
            element={<ScheduleMaintenance />}
          />
          <Route path="work-orders/new" element={<WorkOrderCreate />} />
          <Route path="machines/add" element={<AddMachine />} />
          <Route path="sops/create" element={<CreateSOP />} />
          <Route path="errors" element={<ErrorLogList />} />
          <Route path="sops/new" element={<SOPForm />} />
        </Route>

        {/* AI & KNOWLEDGE GROUP */}
        <Route
          element={<RoleGuard allowedRoles={PERMISSIONS.MANAGE_KNOWLEDGE} />}
        >
          <Route path="knowledge" element={<Knowledge />} />
          <Route path="knowledge/upload" element={<DocumentUpload />} />
          <Route path="knowledge/:id" element={<KnowledgeDetail />} />
          <Route path="predictive" element={<PredictiveInsights />} />
          <Route path="training" element={<Training />} />
          <Route path="workflows" element={<Workflows />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

export default App;
